import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { types as sdkTypes } from '../../util/sdkLoader';
import { getBlockedUserIds } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

// ================ Async Thunks ================ //

const fetchBlockedUsersPayloadCreator = (_, { getState, rejectWithValue, extra: sdk }) => {
  const { currentUser } = getState().user;
  const blockedUserIds = getBlockedUserIds(currentUser);

  if (blockedUserIds.length === 0) {
    return Promise.resolve([]);
  }

  return Promise.all(
    blockedUserIds.map(id =>
      sdk.users
        .show({ id: new UUID(id), include: ['profileImage'] })
        .then(response => {
          const entities = denormalisedResponseEntities(response);
          return entities[0] || null;
        })
        // A blocked user's account may since have been deleted - skip it rather than failing the whole list.
        .catch(() => null)
    )
  ).then(users => users.filter(u => !!u));
};

export const fetchBlockedUsersThunk = createAsyncThunk(
  'BlockedUsersPage/fetchBlockedUsers',
  fetchBlockedUsersPayloadCreator
);

export const fetchBlockedUsers = () => dispatch => dispatch(fetchBlockedUsersThunk());

// ================ Slice ================ //

const initialState = {
  fetchBlockedUsersInProgress: false,
  fetchBlockedUsersError: null,
  blockedUsers: [],
};

const blockedUsersPageSlice = createSlice({
  name: 'BlockedUsersPage',
  initialState,
  reducers: {
    removeBlockedUserFromList(state, action) {
      state.blockedUsers = state.blockedUsers.filter(u => u.id.uuid !== action.payload);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchBlockedUsersThunk.pending, state => {
        state.fetchBlockedUsersInProgress = true;
        state.fetchBlockedUsersError = null;
      })
      .addCase(fetchBlockedUsersThunk.fulfilled, (state, action) => {
        state.fetchBlockedUsersInProgress = false;
        state.blockedUsers = action.payload;
      })
      .addCase(fetchBlockedUsersThunk.rejected, (state, action) => {
        state.fetchBlockedUsersInProgress = false;
        state.fetchBlockedUsersError = storableError(action.error);
      });
  },
});

export const { removeBlockedUserFromList } = blockedUsersPageSlice.actions;

export default blockedUsersPageSlice.reducer;
